export interface Directory {
  name: string;
  items: (File | Directory)[];
}

const readAllEntries = async (directoryReader: FileSystemDirectoryReader, entries = []): Promise<FileSystemEntry[]> => {
  return new Promise(resolve => {
    directoryReader.readEntries(_entries => {
      entries = entries.concat(_entries);
      if (!_entries.length) resolve(entries);
      resolve(readAllEntries(directoryReader, entries));
    });
  });
};

const getFile = async (item: FileSystemEntry): Promise<File> => {
  return new Promise(resolve => {
    item['file']((file: File) => resolve(file));
  });
};

const getWebLink = async (item: DataTransferItem): Promise<string> => {
  return new Promise(resolve => {
    item.getAsString(url => resolve(url));
  });
};

const addDirectory = async (item: FileSystemEntry, files: (File | Directory)[]) => {
  if (!item) return;
  if (item.isDirectory) {
    const directoryFiles: (File | Directory)[] = [];
    const directoryReader = item['createReader']();
    const entries = await readAllEntries(directoryReader);
    await Promise.all(entries.map(async entry => await addDirectory(entry, directoryFiles)));
    files.push({ name: item.name, items: directoryFiles });
  } else {
    const file = await getFile(item);
    if (!file.name.startsWith('.')) files.push(file);
  }
};

export const getFilesFromEvent = async (event: DragEvent): Promise<(File | Directory)[]> => {
  if (event.dataTransfer.files?.length) {
    const files = Array.from(event.dataTransfer.files);

    // if there are no "items" then we can assume they are all files so we just return them
    if (!event.dataTransfer?.items) return files;

    // if all files have extensions, we can assume they are files so we just return them
    if (files.every(file => file.name.includes('.'))) return files;
  }

  // If we get to here, we can assume that the dropped items contain at least one directory, so we need to handle them differently
  const files: (File | Directory)[] = [];
  const items = Array.from(event.dataTransfer.items);

  if (items[0] instanceof DataTransferItem && items[0].kind === 'string') {
    // Drag and drop from the web
    const url = await getWebLink(items[0]);

    if (url.includes('localhost') || url.includes('picflow.com')) {
      return [];
    }

    throw 'from_web_not_allowed';
  } else {
    await Promise.all(items.map(async item => await addDirectory(item.webkitGetAsEntry(), files)));
  }
  return files;
};
